import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Plus } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { SectionLabel } from "@/components/SectionLabel";
import RegisterStrip from "@/components/RegisterStrip";
import {
    COMMITTEES,
    COMMITTEE_GROUPS,
    COMMITTEE_REGISTRATION_FORMS,
    EXECUTIVE_BOARDS,
    PORTFOLIO_MATRIX_URL,
} from "@/data/eventData";

const DETAIL_FIELDS = [
    { key: "about", label: "About" },
    { key: "agenda", label: "Agenda" },
    { key: "executiveBoard", label: "Executive Board" },
    { key: "portfolios", label: "Portfolios" },
    { key: "awards", label: "Awards" },
];

const FieldContent = ({ committee, fieldKey }) => {
    if (fieldKey === "executiveBoard") {
        const board = Array.isArray(committee.executiveBoard)
            ? committee.executiveBoard
            : EXECUTIVE_BOARDS[committee.key] || committee.executiveBoard;
        if (Array.isArray(board)) {
            return (
                <ul className="max-w-xl space-y-2.5">
                    {board.map((member) => (
                        <li
                            key={member.name}
                            className="flex items-baseline justify-between gap-6 border-b border-cream/5 pb-2.5 last:border-b-0"
                        >
                            <span className="font-serif text-lg font-light tracking-tight text-cream">
                                {member.name}
                            </span>
                            <span className="shrink-0 text-right font-sans text-[10px] font-semibold uppercase tracking-[0.25em] text-scarlet">
                                {member.role}
                            </span>
                        </li>
                    ))}
                </ul>
            );
        }
        return (
            <p className="max-w-2xl text-sm font-light leading-relaxed text-ash md:text-base">
                {board}
            </p>
        );
    }
    if (fieldKey === "awards" && Array.isArray(committee.awards)) {
        return (
            <ul className="max-w-xl space-y-2.5">
                {committee.awards.map((award) => (
                    <li
                        key={award.title}
                        className="flex items-baseline justify-between gap-4 border-b border-cream/5 pb-2.5 text-sm font-light text-ash last:border-b-0"
                    >
                        <span>{award.title}</span>
                        <span className="font-sans text-xs font-semibold tracking-wider text-cream/80">
                            {award.amount}
                        </span>
                    </li>
                ))}
            </ul>
        );
    }
    if (fieldKey === "portfolios") {
        if (committee.portfolioText) {
            return (
                <p className="max-w-2xl text-sm font-light leading-relaxed text-ash md:text-base">
                    {committee.portfolioText}
                </p>
            );
        }
        return (
            <a
                href={PORTFOLIO_MATRIX_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`portfolio-matrix-${committee.id}`}
                className="group/pf inline-flex items-center gap-2 text-sm font-light text-cream underline decoration-crimson/60 underline-offset-4 transition-colors duration-300 hover:text-scarlet"
            >
                View the Portfolio Matrix
                <ArrowUpRight className="h-3.5 w-3.5 text-scarlet transition-transform duration-300 group-hover/pf:translate-x-0.5" />
            </a>
        );
    }
    return (
        <p className="max-w-2xl text-sm font-light leading-relaxed text-ash md:text-base">
            {committee[fieldKey]}
        </p>
    );
};

const CommitteeItem = ({ committee, index, open, onToggle }) => {
    const [tab, setTab] = useState("about");

    return (
    <div
        id={`c-${committee.id}`}
        data-testid={`accordion-${committee.id}`}
        className="scroll-mt-28 border-t border-cream/8 last:border-b"
    >
        <button
            type="button"
            onClick={onToggle}
            aria-expanded={open}
            data-testid={`accordion-toggle-${committee.id}`}
            className="group grid w-full grid-cols-[auto_auto_1fr_auto] items-center gap-4 py-6 text-left md:gap-8 md:py-8"
        >
            <span className="font-sans text-[11px] font-semibold tracking-[0.3em] text-scarlet">
                {String(index + 1).padStart(2, "0")}
            </span>
            {committee.logo ? (
                <img
                    src={committee.logo}
                    alt={`${committee.name} emblem`}
                    loading="lazy"
                    className="h-14 w-auto object-contain md:h-16"
                />
            ) : (
                <span className="flex h-14 items-center border border-crimson/40 px-4 font-serif text-xl font-light italic text-scarlet md:h-16">
                    {committee.monogram || committee.name}
                </span>
            )}
            <span>
                <span
                    className={`block font-serif text-3xl font-light tracking-tight transition-colors duration-300 md:text-5xl ${
                        open ? "text-scarlet" : "text-cream group-hover:text-scarlet"
                    }`}
                >
                    {committee.name}
                </span>
                <span className="mt-2 hidden max-w-lg text-sm font-light text-ash md:block">
                    {committee.short}
                </span>
            </span>
            <Plus
                className={`h-5 w-5 justify-self-end text-crimson transition-transform duration-500 ${
                    open ? "rotate-45" : ""
                }`}
            />
        </button>

        <AnimatePresence initial={false}>
            {open && (
                <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                    className="overflow-hidden"
                >
                    <div className="pb-10 md:pl-[52px] lg:pl-[72px]">
                        <div
                            role="tablist"
                            aria-label={`${committee.name} details`}
                            className="no-scrollbar flex gap-7 overflow-x-auto border-b border-cream/8"
                        >
                            {DETAIL_FIELDS.map((field) => {
                                const active = tab === field.key;
                                return (
                                    <button
                                        key={field.key}
                                        type="button"
                                        role="tab"
                                        aria-selected={active}
                                        data-testid={`tab-${committee.id}-${field.key}`}
                                        onClick={() => setTab(field.key)}
                                        className={`relative shrink-0 whitespace-nowrap pb-3.5 pt-1 font-sans text-[10px] font-semibold uppercase tracking-[0.25em] transition-colors duration-300 ${
                                            active
                                                ? "text-cream"
                                                : "text-ash/50 hover:text-cream"
                                        }`}
                                    >
                                        {field.label}
                                        {active && (
                                            <motion.span
                                                layoutId={`tab-underline-${committee.id}`}
                                                transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                                                className="absolute inset-x-0 bottom-0 h-[2px] bg-scarlet"
                                            />
                                        )}
                                    </button>
                                );
                            })}
                        </div>

                        <div
                            data-testid={`tabpanel-${committee.id}`}
                            className="min-h-[120px] pt-7"
                        >
                            <AnimatePresence mode="wait" initial={false}>
                                <motion.div
                                    key={tab}
                                    initial={{ opacity: 0, y: 12 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -8 }}
                                    transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                                >
                                    <FieldContent committee={committee} fieldKey={tab} />
                                </motion.div>
                            </AnimatePresence>
                        </div>

                        <a
                            href={COMMITTEE_REGISTRATION_FORMS[committee.key]}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`register-${committee.id}-btn`}
                            className="group/link mt-8 inline-flex w-full items-center justify-center gap-3 border border-crimson/60 px-7 py-4 font-sans text-[11px] font-semibold uppercase tracking-[0.25em] text-cream transition-colors duration-300 hover:bg-maroon/60 sm:w-auto"
                        >
                            Register for this committee
                            <ArrowRight className="h-4 w-4 text-scarlet transition-transform duration-300 group-hover/link:translate-x-1" />
                        </a>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    </div>
    );
};

export default function Committees() {
    const [openId, setOpenId] = useState(null);
    const location = useLocation();

    useEffect(() => {
        if (location.hash.startsWith("#c-")) {
            setOpenId(location.hash.replace("#c-", ""));
        }
    }, [location.hash]);

    return (
        <>
            <section className="relative overflow-hidden pb-16 pt-32 md:pb-24 md:pt-44">
                <div
                    aria-hidden="true"
                    className="absolute inset-0 bg-[radial-gradient(ellipse_80%_55%_at_50%_-10%,rgba(201,31,38,0.16),transparent_62%)]"
                />
                <div className="relative mx-auto max-w-[1400px] px-5 md:px-10">
                    <Reveal y={20}>
                        <SectionLabel index="02" text="Seven Committees · One Auction" />
                    </Reveal>
                    <Reveal delay={0.1} y={20}>
                        <h1
                            data-testid="committees-heading"
                            className="mt-8 max-w-4xl font-serif text-5xl font-light leading-[1.02] tracking-tight text-cream sm:text-6xl lg:text-7xl"
                        >
                            The Rooms Where
                            <br />
                            Ideas Take <em className="italic text-scarlet">Shape.</em>
                        </h1>
                    </Reveal>
                    <Reveal delay={0.2} y={20}>
                        <p className="mt-8 max-w-xl text-base font-light leading-relaxed text-ash md:text-lg">
                            Every committee opens into its own world — agenda, executive
                            board, portfolios and awards. Detailed matrices are announced
                            by the Secretariat as the conference approaches.
                        </p>
                    </Reveal>
                </div>
            </section>

            {COMMITTEE_GROUPS.map((group) => (
                <section
                    key={group.id}
                    id={group.id}
                    data-testid={`committee-group-${group.id}`}
                    className="scroll-mt-28 pb-16 md:pb-24"
                >
                    <div className="mx-auto max-w-[1400px] px-5 md:px-10">
                        <Reveal>
                            <div className="mb-4 flex items-center gap-4">
                                <span className="h-px w-10 bg-crimson/60" aria-hidden="true" />
                                <h2 className="font-sans text-[11px] font-semibold uppercase tracking-[0.35em] text-ash md:text-xs">
                                    {group.title}
                                </h2>
                            </div>
                        </Reveal>
                        {group.committeeIds.map((id, i) => {
                            const committee = COMMITTEES.find((c) => c.id === id);
                            return (
                                <CommitteeItem
                                    key={id}
                                    committee={committee}
                                    index={i}
                                    open={openId === id}
                                    onToggle={() => setOpenId(openId === id ? null : id)}
                                />
                            );
                        })}
                    </div>
                </section>
            ))}

            <RegisterStrip text="Chosen your room? Secure your seat at I-ACE Chapter 4." />
        </>
    );
}
